import { getEngravingSetting } from '@/apis/engravingSettings/index'
import { getSaeSetting } from '@/apis/equipment/index'
import { fetchGetProductionNcSetting } from '@/apis/materialList'
import { getFileStartNum } from '@/apis/tag'
import router from '@/router'
import store from '@/store'
import { Message, getUrlParams } from '@/util/commonFuncs'
import { fileNameSetObj } from '@/util/configure'
import { savaFileToDisk } from '@/util/fileSystem/generateFile'
import JsZip from '@/util/jszip'
import { getByToken, postByToken } from '@/util/vueRequest'
import {
  checkIsMergeFolder,
  getChildrenFolderFileSize,
  getFolderClassifyNameByMatCodeTextureThickSize,
} from '@/views/newPaiban/util/downLoadNC'
import { dealElectronicSawData } from '@/views/newPaiban/util/downLoadNC'
import { uploadCompressedFile } from '@/views/newPaiban/util/uploadFiles'
import { jsonToFile, uploadFileToOss } from '@yige/utils'
import OSS from 'ali-oss'
import axios from 'axios'
import FileSaver from 'file-saver'
import Vue from 'vue'

import { dealHighlightMatCode, matchHighlightBoardName } from './commonFun'
import { transformToEzSort } from './connect-color-label/transform'
import { compressJsonToGzip } from './gzipCompress'

const IP_CACHE_KEY = 'user_ip'
const IPV4_REG = /(?:\d{1,3}\.){3}\d{1,3}/

/** 校验并提取 IPv4 */
function pickIpv4(val) {
  if (!val) return ''
  const match = String(val).match(IPV4_REG)
  return match ? match[0] : ''
}

/**
 * 通过公网服务获取当前出口 IP（get_user_info 不返回 ip，需前端自行获取）
 * @returns {Promise<string>}
 */
async function fetchPublicIpByHttp() {
  const sources = [
    async () => {
      const { data } = await axios.get('https://api.ipify.org', {
        params: { format: 'json' },
        timeout: 4000,
      })
      return pickIpv4(data?.ip)
    },
    async () => {
      const { data } = await axios.get('https://api.ip.sb/ip', {
        timeout: 4000,
        responseType: 'text',
      })
      return pickIpv4(data)
    },
    async () => {
      const { data } = await axios.get(
        'https://whois.pconline.com.cn/ipJson.jsp',
        {
          params: { json: true },
          timeout: 4000,
        }
      )
      return pickIpv4(data?.ip)
    },
  ]
  for (const fetchIp of sources) {
    try {
      const ip = await fetchIp()
      if (ip) return ip
    } catch (e) {
      // 尝试下一个来源
    }
  }
  return ''
}

/**
 * WebRTC STUN 兜底（部分环境可拿到公网/内网 IP）
 * @returns {Promise<string>}
 */
function fetchIpByWebRtc() {
  return new Promise((resolve) => {
    try {
      const RTCPeerConnection =
        window.RTCPeerConnection ||
        window.webkitRTCPeerConnection ||
        window.mozRTCPeerConnection
      if (!RTCPeerConnection) {
        resolve('')
        return
      }
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      })
      let resolved = false
      function done(ip) {
        if (resolved) return
        resolved = true
        try {
          pc.close()
        } catch (e) {
          // ignore
        }
        resolve(ip || '')
      }
      pc.createDataChannel('')
      pc.onicecandidate = (event) => {
        if (!event?.candidate?.candidate) return
        const ip = pickIpv4(event.candidate.candidate)
        // 跳过常见内网段优先等公网；若最终没有公网再接受内网
        if (ip && !/^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(ip)) {
          done(ip)
        }
      }
      pc.createOffer()
        .then((offer) => pc.setLocalDescription(offer))
        .catch(() => done(''))
      setTimeout(() => {
        // 超时后尝试从 localDescription 解析，或返回空
        const sdp = pc.localDescription?.sdp || ''
        done(pickIpv4(sdp))
      }, 2500)
    } catch (e) {
      resolve('')
    }
  })
}

/** 获取当前用户 IP，结果写入 sessionStorage 缓存 */
export async function fetchUserIp() {
  const cachedIp =
    sessionStorage.getItem(IP_CACHE_KEY) || store.state.userInfo?.request_ip
  if (cachedIp) return cachedIp

  let ip = await fetchPublicIpByHttp()
  if (!ip) {
    ip = await fetchIpByWebRtc()
  }
  if (ip) {
    sessionStorage.setItem(IP_CACHE_KEY, ip)
  }
  return ip || ''
}

/**
 * 给 NC 导出的 json Blob 写入用户 IP
 * @param {Blob} jsonBlob
 * @returns {Promise<Blob>}
 */
export async function appendIpToJsonBlob(jsonBlob) {
  if (!jsonBlob) return jsonBlob
  try {
    const text = await jsonBlob.text()
    const jsonObj = JSON.parse(text)
    if (!jsonObj.ip) {
      jsonObj.ip = await fetchUserIp()
    }
    if (jsonObj.ip) {
      sessionStorage.setItem(IP_CACHE_KEY, jsonObj.ip)
    }
    return new Blob([JSON.stringify(jsonObj)], {
      type: jsonBlob.type || '',
    })
  } catch (e) {
    return jsonBlob
  }
}

/**
 * 对阿里云 OSS URL 做百分号编码，防止路径中的特殊字符（如 #）被浏览器当作 fragment 截断，导致请求 404 无法下载
 * 只处理 eggrj OSS 域名；路径中已包含百分号转义（%xx）的 URL 视为已编码，直接透传避免重复编码
 * @param {string} url
 * @returns {string}
 */
export function encodeOssUrl(url) {
  if (!url || typeof url !== 'string') return url
  const match = url.match(
    /^(https?:\/\/eggrj\.oss-cn-hangzhou\.aliyuncs\.com\/)(.*)$/
  )
  if (!match) return url
  const [, prefix, path] = match
  if (/%[0-9a-fA-F]{2}/.test(path)) return url
  return prefix + encodeURIComponent(path)
}

// 获取文件数据
function _getFile(url) {
  return new Promise((resolve) => {
    axios({
      method: 'get',
      url,
      responseType: 'arraybuffer',
    })
      .then((data) => {
        resolve(data.data)
      })

      .catch(() => {
        resolve(false)
      })
  })
}

/**
 * 根据value获取key
 * @param {*} obj
 * @param {*} value
 * @returns
 */
export function _getKey(obj, value) {
  for (let key in obj) {
    if (obj[key] === value) {
      return key
    }
  }
}

/**
 * 判断是否在文件合并中
 * @param {*} folderSetting
 * @param {*} type
 * @returns
 */
function _isIncludeMerge(folderSetting, type) {
  // 第一步：通过fileType获得 key
  // 第二步：然后通过key获取到 fileNameSetObj 的值 value
  // 第三部：通过value判断是都在 mergeFolderList 里面
  let value = fileNameSetObj[_getKey(folderSetting, type)] + '文件'
  const mergeFolderList = folderSetting.merge_folder_list
  return mergeFolderList.includes(value)
}

/**
 * 转换文件名
 * @param {*} initName
 * @param {*} folderSetting
 * @param {*} key
 * @returns
 */
export function _translateFileNameFn(
  initName,
  folderSetting = {},
  key,
  itemData = null,
  isPaibanImg = false
) {
  const {
    plank_type = '',
    filetype = '',
    fileType = '',
    exportType = '',
    isOnlySurplus = false,
    surplusInfo = false,
  } = itemData || {}
  const { is_gen_surplus_folder } = folderSetting
  const tagFile = exportType === 'pdf' && isOnlySurplus
  let needMerge = folderSetting.merge_folder_list?.length > 1
  if (is_gen_surplus_folder) {
    if (
      (plank_type === 'surplus' && fileType !== 'tag') ||
      tagFile ||
      surplusInfo
    ) {
      needMerge = false
    }
  }

  // 如果为true，那么就不使用合并文件(玻璃切割机)
  if (folderSetting.use_category_folder) {
    needMerge = false
  }

  let translateFileName = initName
  const mergeFolderDisplayName = folderSetting.merge_folder_display_name
  if (key && folderSetting[key]) {
    translateFileName = folderSetting[key]
  }
  let paibanList = ['排版图1', '排版图2', '排版图3']
  // 自定义排版图 文件夹处理
  if (
    store.state.preferencesSetting.setting?.isCustomPaibanImg &&
    isPaibanImg
  ) {
    if (folderSetting?.required_folders?.includes('paibanImg_folder'))
      translateFileName =
        itemData?.type !== 'pdf'
          ? folderSetting?.paibanImg_folder + '/' + key
          : folderSetting?.paibanImg_folder
    paibanList = []
    paibanList.push(key)
  }
  // 生成的文件对应的类型和名称都需要存储起来
  // 先获取当前文件的内容
  let currentFolderContents = JSON.parse(
    JSON.stringify(store.state.folderContents)
  )
  let translateFileNameCopy = translateFileName //保存一个数据副本
  if (key && translateFileName) {
    if (
      paibanList.indexOf(key) === -1 &&
      _getKey(folderSetting, translateFileName) &&
      ((folderSetting.required_folders?.indexOf(
        _getKey(folderSetting, translateFileName)
      ) === -1 &&
        key === translateFileName) ||
        (folderSetting.required_folders?.indexOf(key) === -1 &&
          key !== translateFileName))
    ) {
      translateFileName = ''
    } else {
      if (key === translateFileName) {
        currentFolderContents[_getKey(folderSetting, translateFileName)] =
          translateFileName
      } else {
        currentFolderContents[key] = translateFileName
      }

      store.commit(
        'setFolderContents',
        JSON.parse(JSON.stringify(currentFolderContents))
      )
    }
  }
  // 处理需要合并的情况
  const mergeFolderList = folderSetting.merge_folder_list
  if (needMerge && paibanList.indexOf(key) !== -1) {
    if (mergeFolderList.includes(key + '文件')) {
      translateFileName = mergeFolderDisplayName
    }
  } else if (
    needMerge &&
    _isIncludeMerge(folderSetting, translateFileNameCopy)
  ) {
    translateFileName = mergeFolderDisplayName
    if (
      mergeFolderList.length > 1 &&
      folderSetting.platePropertyMergeFile &&
      itemData &&
      mergeFolderList.includes(translateFileNameCopy + '文件')
    ) {
      // 如果满足子文件夹拼接的条件
      translateFileName += dealTransFileName(itemData)
    }
  }
  return translateFileName
}

/**
 * 请求nc下载文件
 * @param {any []} arr 文件数组
 * @param {number} shuntNum 请求分流量
 */
async function genNcFileRequest(
  arr,
  shuntNum = 50,
  zip = new JsZip(),
  cache = {},
  name,
  folderSetting
) {
  const num = Math.ceil(arr.length / shuntNum)
  const shuntRequestList = []
  const fileResultList = []
  // 将数据分割
  for (let index = 0; index < num; index++) {
    const fileList = arr.slice(index * shuntNum, (index + 1) * shuntNum)
    shuntRequestList.push(fileList)
  }
  const len = shuntRequestList.length
  let exeNum = 0
  const _loopRequest = async (fileList) => {
    if (exeNum >= len) return
    exeNum++
    const urlList = fileList.map((item) => {
      let url = ['tag', 'excel'].includes(item.fileType)
        ? encodeOssUrl(item.url)
        : 'https://eggrj.oss-cn-hangzhou.aliyuncs.com/' +
          encodeURIComponent(item.filename)
      let fileData = item.fileData

      return fileData
        ? tagFileExport(
            item,
            zip,
            cache,
            name,
            folderSetting,
            item.fileType == 'excel'
          )
        : _getFile(url)
      // return _getFile(url)
    })
    const result = await Promise.all(urlList)
    /**
     * promise.all返回值顺序是传入的顺序，执行是谁先返回谁先执行
     * 所以直接通过下标访问,将对应的文件数据保存
     */
    fileList.forEach((item, idx) => {
      item.fileBlobResult = result[idx]
    })
    fileResultList.push(fileList)
    await _loopRequest(shuntRequestList.shift())
  }
  await _loopRequest(shuntRequestList.shift())
  return fileResultList.flat(2)
}

export function removeHeadAndTail(str) {
  if (str.includes('高光_')) {
    const parts = str.split('_')
    // 找到高光所在的位置，通过后面的材质进行判断
    const index = parts.findIndex((part) => part === '高光')
    parts[index] =
      matchHighlightBoardName(parts[index + 1], false) || parts[index + 1]
    return parts.join('_')
  }
  return str
}
export async function genContentPromises(
  arr,
  folderSetting,
  isNeedRecordSize2,
  jsonBlob,
  workerShopBlob,
  name,
  setting_id
) {
  // 下载 NC 导出的 json 写入用户 IP
  jsonBlob = await appendIpToJsonBlob(jsonBlob)

  let sawSetting
  let saw_setting_res = await getSaeSetting({
    setting_id: store.state.settingId || setting_id,
    setting_type: 'electronicSaw',
  })
  if (saw_setting_res.data.data) sawSetting = saw_setting_res.data.data

  let ncSetting = store.state.ncSetting
  // 获取生产线配置,解决刷新导致useCategoryFolder和categoryFolderId丢失的问题
  if (setting_id) {
    const nc_setting_res = await fetchGetProductionNcSetting({
      setting_id,
    })
    if (nc_setting_res.data) ncSetting = nc_setting_res.data
  }
  // 兼容从车间下载
  const isNeedRecordSize = true
  let num = 1
  let ncSuffix = []
  let zip = new JsZip()
  const cache = {}
  const {
    useCategoryFolder,
    same_size_sheet_cutting_files,
    label_image_in_nc_folder,
    use_category_size_folder,
  } = ncSetting.folderCategory
  const hasLabelImageInNcFolderPermission =
    store.state.userPermissions['label_image_in_nc_folder'] &&
    label_image_in_nc_folder
  const data = await genNcFileRequest(arr, 50, zip, cache, name, folderSetting)
  let res = await getEngravingSetting({ setting_id })
  // 双工位合并
  let doubleStationCombine =
    res.data.ncOutputSetting?.fileMerge?.doubleStationCombine ?? false
  const ncGropArr = groupNCData(data)
  const isOnlyStandard = !ncGropArr[0].length
  const hasSurplus = ncGropArr[1].some((item) => {
    const stockKey = item.stock_key
      .split(':')
      .map((i) => {
        if (Number(i)) {
          return Number(i)
        } else return i
      })
      .slice(0, -1)
      .join(':')
    return Object.hasOwnProperty.call(store.state.surplusParams, stockKey)
  })
  // 特殊
  ncGropArr[0].forEach((item) => {
    // 对于特殊大板勾了材质就生成材质文件夹，没勾就不生成。
    // 勾选了尺寸的话，就生成尺寸文件夹，没勾就不生成
    let filename = ''
    let nextFileName = ''
    let plankSize = ''
    const stockKey =
      item.stock_key?.substring(0, item.stock_key.lastIndexOf(':')) ?? ''
    // 勾了材质
    if (ncSetting.folderCategory && useCategoryFolder) {
      // 截取stock_key，获取颜色、材质、厚度相同的文件放入相同文件夹
      nextFileName = stockKey.replace(/:/g, '_') + '/'
    }
    // 按照材质颜色厚度尺寸得到其文件夹名称，优先级比颜色材质厚度高
    if (use_category_size_folder) {
      nextFileName = `${
        getFolderClassifyNameByMatCodeTextureThickSize(
          stockKey,
          item.stock_num
        ) ?? nextFileName
      }/`
    }
    nextFileName = removeHeadAndTail(nextFileName)
    if (!doubleStationCombine) {
      // 勾了尺寸
      if (same_size_sheet_cutting_files) {
        plankSize = `${item.big_plank_size.width}×${item.big_plank_size.height}/`
      }
      // 都勾选了
      if (same_size_sheet_cutting_files && useCategoryFolder) {
        plankSize = `${item.big_plank_size.width}×${item.big_plank_size.height}/`
      }
    }
    generateFolder(
      item,
      nextFileName,
      useCategoryFolder,
      same_size_sheet_cutting_files,
      filename,
      folderSetting,
      zip,
      jsonBlob,
      workerShopBlob,
      cache,
      plankSize,
      num,
      name
    )
  })
  // 余料/标准
  ncGropArr[1].forEach((item) => {
    let filename = ''
    let nextFileName = ''
    let plankSize = ''
    const stockKey =
      item.stock_key?.substring(0, item.stock_key.lastIndexOf(':')) ?? ''
    // 勾了材质
    if (ncSetting.folderCategory && useCategoryFolder) {
      // 截取stock_key，获取颜色、材质、厚度相同的文件放入相同文件夹
      nextFileName = stockKey.replace(/:/g, '_') + '/'
    }
    // 按照材质颜色厚度尺寸得到其文件夹名称，优先级比颜色材质厚度高
    if (use_category_size_folder) {
      nextFileName = `${
        getFolderClassifyNameByMatCodeTextureThickSize(
          stockKey,
          item.stock_num
        ) ?? nextFileName
      }/`
    }
    nextFileName = removeHeadAndTail(nextFileName)
    if (!doubleStationCombine) {
      // 勾了尺寸
      if (same_size_sheet_cutting_files) {
        if (!isOnlyStandard) {
          if (hasSurplus) {
            plankSize = `${item.big_plank_size.width}×${item.big_plank_size.height}（余料）/`
          } else {
            plankSize = `${item.big_plank_size.width}×${item.big_plank_size.height}/`
          }
        }
      }
      const stockKey = item.stock_key
        .split(':')
        .map((i) => {
          if (Number(i)) {
            return Number(i)
          } else return i
        })
        .slice(0, -1)
        .join(':')
      // 尺寸和材质都勾了
      if (same_size_sheet_cutting_files && useCategoryFolder) {
        if (isNeedRecordSize && !doubleStationCombine) {
          if (!isOnlyStandard) {
            if (
              Object.hasOwnProperty.call(store.state.surplusParams, stockKey)
            ) {
              plankSize = `${item.big_plank_size.width}×${item.big_plank_size.height}（余料）/`
            } else {
              plankSize = `${item.big_plank_size.width}×${item.big_plank_size.height}/`
            }
          }
        }
      }
    }

    generateFolder(
      item,
      nextFileName,
      useCategoryFolder,
      same_size_sheet_cutting_files,
      filename,
      folderSetting,
      zip,
      jsonBlob,
      workerShopBlob,
      cache,
      plankSize,
      num,
      name
    )
  })
  const { orderType, orderCounts, OrderTypeMap } = dealElectronicSawData(data)
  const { is_gen_surplus_folder } = folderSetting // 是否生成余料文件夹

  data.forEach((item) => {
    if (!item.fileBlobResult) return
    let filename = ''
    let nextFileName = ''
    const isLabelOrNC = item.filetype === 'label' || item.filetype === 'nc'
    const surplusFolder = _translateFileNameFn(
      '余料大板文件',
      folderSetting,
      'surplus_folder',
      item
    )

    // 在下载文件之前对文件夹进行一下分类的设定
    if (isLabelOrNC && ncSetting.folderCategory && useCategoryFolder) {
      const stockKey =
        item.stock_key?.substring(0, item.stock_key.lastIndexOf(':')) ?? ''
      // 截取stock_key，获取颜色、材质、厚度相同的文件放入相同文件夹
      nextFileName = stockKey.replace(/:/g, '_') + '/'
    }
    // 按照材质颜色厚度尺寸得到其文件夹名称，优先级比颜色材质厚度高
    if (isLabelOrNC && use_category_size_folder) {
      const stockKey =
        item.stock_key?.substring(0, item.stock_key.lastIndexOf(':')) ?? ''
      nextFileName = `${
        getFolderClassifyNameByMatCodeTextureThickSize(
          stockKey,
          item.stock_num
        ) ?? nextFileName
      }/`
    }
    nextFileName = removeHeadAndTail(nextFileName)
    if (item.fileType == 'tag') {
      let translateFileName = _translateFileNameFn(
        '标签图',
        folderSetting,
        'label_image_folder',
        item
      )
      if (hasLabelImageInNcFolderPermission && store.state.isFollowExportTag) {
        translateFileName = _translateFileNameFn(
          '下料文件',
          folderSetting,
          'engraving_folder',
          item
        )
        translateFileName = translateFileName
          ? translateFileName + '/label/image/'
          : ''
      } else {
        // 解释一下这里，余料文件夹单独生成，条件就是开启了 余料文件大板设置 并且 是余料plank_type==='surplus'
        translateFileName =
          is_gen_surplus_folder && item.isOnlySurplus
            ? surplusFolder
              ? `${surplusFolder}/${translateFileName}/`
              : ''
            : translateFileName
            ? `${translateFileName}/`
            : ''
      }
      let afterType = item.url.endsWith('.pdf') ? '.pdf' : '.zip' //修改.zip文件
      filename =
        (nextFileName === '/' ? '' : nextFileName) +
        translateFileName +
        name +
        afterType
    } else if (item.fileType == 'excel') {
      filename = name + '.xlsx'
    } else if (item.filetype == 'label') {
      let translateFileName = _translateFileNameFn(
        '贴标机',
        folderSetting,
        'labeling_folder',
        item
      )
      if (hasLabelImageInNcFolderPermission && store.state.isFollowExportTag) {
        translateFileName = _translateFileNameFn(
          '下料文件',
          folderSetting,
          'engraving_folder',
          item
        )
        translateFileName = translateFileName
          ? translateFileName + '/label/'
          : ''
      } else {
        translateFileName =
          is_gen_surplus_folder && item.plank_type === 'surplus'
            ? surplusFolder
              ? `${surplusFolder}/${translateFileName}/`
              : ''
            : translateFileName
            ? `${translateFileName}/`
            : ''
      }
      // 子文件夹合并
      if (item.stock_key && checkIsMergeFolder('贴标机文件')) {
        const templateString = dealTransFileName(item)
        translateFileName += templateString
      }
      ncSuffix.push(item.filename.split('.').slice(-1)[0])
      // 贴标机文件插入到下料文件F里面
      /**
       * 1、开启 将雕刻机正/反面文件用不同文件夹放置 功能 且不处理晓风贴标机
       * 2、只区分到材质 不区分尺寸
       */
      if (
        folderSetting.engraving_front_back_folder &&
        !(
          ncSetting.label_img_big_plank_folder && ncSetting.labeling_file_insert
        )
      ) {
        const engravingTranslateFileName = genEngravingFileName(
          item,
          folderSetting
        )
        filename =
          engravingTranslateFileName +
          'F/' +
          nextFileName +
          item.file_display_name
      } else if (is_gen_surplus_folder && item.plank_type === 'surplus') {
        filename = translateFileName + item.file_display_name
      } else {
        filename =
          (nextFileName === '/' ? '' : nextFileName) +
          translateFileName +
          item.file_display_name
      }
    } else if (item.filetype == 'nc') {
      return
    } else if (item.file_type == '玻璃切割机') {
      // filename = nextFileName + '玻璃切割机/' + item.file_display_name
      const { use_category_folder } = store.state.ncSetting.glass_setting ?? {}
      let fName = '玻璃切割机/'
      if (use_category_folder) {
        fName = item.stock_key.replace(/:/g, '_') + '/'
      }
      filename = nextFileName + fName + item.file_display_name
    } else if (item.file_type === 'saw') {
      let translateFileName = _translateFileNameFn(
        'saw',
        folderSetting,
        'electronic_saw_folder'
      )
      translateFileName = translateFileName ? `${translateFileName}/` : ''

      let delimiter = sawSetting?.delimiter || ''
      let lv2Folder = ''
      let lv3Folder = ''
      let nextFileName = ''

      if (sawSetting.rooms_by_category) {
        if (sawSetting.useCategoryFolder) {
          switch (orderType) {
            case OrderTypeMap['noOrder']:
            case OrderTypeMap['singleOrder']:
              if (item.room_name) {
                const newRemark = item.room_remark
                  ? item.room_remark.replace(/[/:?<>\|."*]/g, '')
                  : ''
                nextFileName = `${item.room_name}${
                  newRemark ? delimiter + newRemark : ''
                }`
              } else if (item.partHasRoom) {
                nextFileName = '其他房间'
              }
              break
            case OrderTypeMap['multiOrder']:
              if (item.order_no) {
                const newCustomer = item.customer_name
                  ? item.customer_name.replace(/[/:?<>\|."*]/g, '')
                  : ''
                lv2Folder = `${item.order_no}${
                  newCustomer ? delimiter + newCustomer : ''
                }`
              } else {
                lv2Folder = '其他订单'
              }
              if (item.room_name) {
                const newRemark = item.room_remark
                  ? item.room_remark.replace(/[/:?<>\|."*]/g, '')
                  : ''
                lv3Folder = `${item.room_name}${
                  newRemark ? delimiter + newRemark : ''
                }`
              } else if (
                orderCounts[item.order_no]?.length !== 1 &&
                orderCounts[item.order_no].some((item) => item.room_name)
              ) {
                lv3Folder = item.partHasRoom ? '其他房间' : ''
              }
              nextFileName = `${lv2Folder}${lv3Folder ? '/' + lv3Folder : ''}`
              break
            default:
              break
          }
        } else {
          switch (orderType) {
            case OrderTypeMap['noOrder']:
              break
            case OrderTypeMap['singleOrder']:
              break
            case OrderTypeMap['multiOrder']:
              if (item?.partHasOrder) {
                nextFileName = '其他订单'
              } else {
                const newCustomer = item.customer_name
                  ? item.customer_name.replace(/[/:?<>\|."*]/g, '')
                  : ''
                nextFileName = `${item.order_no}${
                  newCustomer ? delimiter + newCustomer : ''
                }`
              }
              break
            default:
              break
          }
        }
      }

      translateFileName += nextFileName ? `${nextFileName}/` : ''

      if (item.file_display_name) {
        filename = `${translateFileName}${item.file_display_name}`
      } else {
        filename = `${translateFileName}${item.filename.split('/')[1]}`
      }

      filename = dealGroupFile(item, filename)
    } else {
      let translateFileName = _translateFileNameFn(
        item.filetype,
        folderSetting,
        item.filetype,
        item
      )
      translateFileName =
        is_gen_surplus_folder && item.plank_type === 'surplus'
          ? `${surplusFolder}/${translateFileName}/`
          : translateFileName
          ? `${translateFileName}/`
          : ''
      if (item.file_display_name) {
        filename = translateFileName + item.file_display_name
      } else {
        filename = translateFileName + item.filename.split('/')[1]
      }
      filename = dealGroupFile(item, filename)
    }

    let nameList = filename.split('/')
    const ignoreList = [
      'PTP_folder',
      'dock5F_drill_folder',
      'dock6F_drill_folder',
      'door_wall_aio_folder',
      'side_hole_folder',
      'electronic_saw_folder',
    ].map((item) => (folderSetting ? folderSetting[item] : ''))
    if (nameList.length) {
      if (
        nameList[0] === folderSetting?.merge_folder_display_name &&
        nameList[1] &&
        store.state.ncSetting?.customizeFolder?.merge_folder_list.length > 1 &&
        !ignoreList.includes(
          item.filetype ?? folderSetting['electronic_saw_folder']
        )
      ) {
        if (store.state.tempFileName.includes(nameList[nameList.length - 1])) {
          let tempList = JSON.parse(JSON.stringify(store.state.repeatFileName))
          tempList.push(nameList[nameList.length - 1])
          store.commit('setRepeatFileName', tempList)
        } else {
          let tempList = JSON.parse(JSON.stringify(store.state.tempFileName))
          tempList.push(nameList[nameList.length - 1])
          store.commit('setTempFileName', tempList)
        }
      }
    }
    const newFileName = dealJinxiPaibanFolder(filename, surplusFolder)
    zip.file(newFileName, item.fileBlobResult, { binary: true }) // 逐个添加文件
    if (jsonBlob) {
      zip.file(`${name}.json`, jsonBlob)
    }
    if (workerShopBlob) {
      zip.file(`${name}.xlsx`, workerShopBlob, { binary: true })
    }
    cache[newFileName] = item.fileBlobResult
  })
  return {
    zip,
    cache,
    promises: data,
    ncSuffix,
  }
}

// 精细排版下对文件夹的处理
export function dealJinxiPaibanFolder(filename, surplusFolder = '') {
  const isJinxiPaiban = router.currentRoute.query?.is_jinxi_paiban === '1'
  if (!isJinxiPaiban) return filename
  const isSurplusName = surplusFolder && filename.indexOf(surplusFolder) !== -1
  if (isSurplusName) {
    const nameArr = filename.split('/')
    nameArr.shift()
    return nameArr.join('/')
  }
  return filename
}

// 返回二维数组 0:特殊大板 1:余料/标准
function groupNCData(arr) {
  const ncArr = arr.filter((item) => item.filetype === 'nc')
  return ncArr.reduce(
    (acc, cur) => {
      if (!acc[0]) acc[0] = []
      if (!acc[1]) acc[1] = []
      if (cur.big_plank_size && cur.big_plank_size.otherPlate) {
        acc[0].push(cur)
      } else {
        acc[1].push(cur)
      }
      return acc
    },
    [[], []]
  )
}
// 提取下面generateFolder方法里面 生成下料文件translateFileName 的代码
export function genEngravingFileName(item, folderSetting, surplusFolder) {
  const { is_gen_surplus_folder } = folderSetting
  let translateFileName = _translateFileNameFn(
    '下料文件',
    folderSetting,
    'engraving_folder',
    item
  )
  // 如当前nc的文件类型实际上为anc(因为nc和anc文件的filetype相同都是nc),同时文件合并设置开启了一体机的文件名则按照一体机文件名的方式进行生成文件名称
  if (isUseAioFolder(folderSetting, item)) {
    translateFileName = _translateFileNameFn(
      '一体机文件',
      folderSetting,
      'aio_folder'
    )
  }
  translateFileName =
    is_gen_surplus_folder && item.plank_type === 'surplus'
      ? surplusFolder
        ? `${surplusFolder}/${translateFileName}/`
        : ''
      : translateFileName
      ? `${translateFileName}/`
      : ''

  if (item.stock_key && checkIsMergeFolder('雕刻机文件')) {
    if (!checkIsMergeFolder('雕刻机文件')) {
      if (!is_gen_surplus_folder && item.plank_type !== 'surplus') {
        filename += plankSize
      }
    }
    const templateString = dealTransFileName(item)
    translateFileName += templateString
  }
  return translateFileName
}
function generateFolder(
  item,
  nextFileName,
  useCategoryFolder,
  same_size_sheet_cutting_files,
  filename,
  folderSetting,
  zip,
  jsonBlob,
  workerShopBlob,
  cache,
  plankSize,
  num,
  name
) {
  const { is_gen_surplus_folder } = folderSetting
  const surplusFolder = _translateFileNameFn(
    '余料大板文件夹',
    folderSetting,
    'surplus_folder',
    item
  )
  let translateFileName = genEngravingFileName(
    item,
    folderSetting,
    surplusFolder
  )
  let workstationFileName = ''
  // 将雕刻机正/反面文件用不同文件夹放置
  // 先分正反 再分材质
  /**
   * 1、开启这个功能
   * 2、根据direction 分成正反(Z/F)两个文件夹
   */
  if (folderSetting.engraving_front_back_folder) {
    filename = translateFileName
    if (item.direction === 'front') {
      filename += 'Z/' + nextFileName + 'Z/'
    } else {
      filename += 'F/' + nextFileName
      // 一体机文件再加一层F
      if (item?.aio_file_type === 'aio') {
        filename += 'F/'
      }
    }
  } else {
    const { single_double_station, fileMerge } = store.state.ncSetting
    // 开启雕刻机按工位分文件夹功能 且 勾选双工位 且 双工位合并和单工位合并未开启 则按照工位分文件夹
    if (
      folderSetting.class_by_workstations &&
      single_double_station === 'double' &&
      !fileMerge?.doubleStationCombine &&
      !fileMerge?.singleStationCombine
    ) {
      if (item.workstation === 'odd') {
        workstationFileName = '工位一/'
      } else if (item.workstation === 'even') {
        workstationFileName = '工位二/'
      }
    }

    filename =
      is_gen_surplus_folder && item.plank_type === 'surplus'
        ? translateFileName
        : nextFileName + translateFileName
  }

  if (!checkIsMergeFolder('雕刻机文件')) {
    if (
      (useCategoryFolder && same_size_sheet_cutting_files) ||
      (!useCategoryFolder && same_size_sheet_cutting_files)
    ) {
      // if (!is_gen_surplus_folder && item.plank_type !== 'surplus') {
      filename += plankSize
      // }
    }
  }
  // 不同工位分文件夹 在材质、尺寸等分了之后再添加工位文件夹
  filename += workstationFileName
  if (item.file_display_name == '') {
    filename =
      filename +
      ('00' + num).substr(-3, 3) +
      '' +
      (item.direction == 'front' ? '正.' : '反.') +
      item.filetype
    num++
  } else {
    filename += item.file_display_name
  }

  let nameList = filename.split('/')
  const ignoreList = [
    'PTP_folder',
    'dock5F_drill_folder',
    'dock6F_drill_folder',
    'door_wall_aio_folder',
    'side_hole_folder',
  ].map((item) => (folderSetting ? folderSetting[item] : ''))
  if (nameList.length) {
    if (
      nameList[0] === folderSetting?.merge_folder_display_name &&
      nameList[1] &&
      store.state.ncSetting?.customizeFolder?.merge_folder_list.length > 1 &&
      !ignoreList.includes(item.filetype)
    ) {
      if (store.state.tempFileName.includes(nameList[nameList.length - 1])) {
        let tempList = JSON.parse(JSON.stringify(store.state.repeatFileName))
        tempList.push(nameList[nameList.length - 1])
        store.commit('setRepeatFileName', tempList)
      } else {
        let tempList = JSON.parse(JSON.stringify(store.state.tempFileName))
        tempList.push(nameList[nameList.length - 1])
        store.commit('setTempFileName', tempList)
      }
    }
  }
  const newFileName = dealJinxiPaibanFolder(filename, surplusFolder)
  zip.file(newFileName, item.fileBlobResult, { binary: true }) // 逐个添加文件
  if (jsonBlob) {
    zip.file(`${name}.json`, jsonBlob)
  }
  if (workerShopBlob) {
    zip.file(`${name}.xlsx`, workerShopBlob, { binary: true })
  }
  cache[newFileName] = item.fileBlobResult
}
export async function genMergeSubFolderData(
  data,
  isNeedSplice = true,
  ncSetting,
  flag
) {
  const date = new Date()
  const mounth =
    date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1
  const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()

  const stockKeyArr = Array.from(
    new Set(
      data
        .filter((d) => d.stockKey || d.stock_key)
        .map((item) => {
          let sizeList
          let size
          if (flag == 'paibanData') {
            size = getChildrenFolderFileSize(item, store.state.ncSetting, '×')
          } else {
            if (item.display_big_plank_size) {
              sizeList = item.display_big_plank_size.split('*').splice(0, 2)
              size = `${sizeList[0]}×${sizeList[1]}`
            }
          }
          let stock_key = item.stockKey ?? item.stock_key
          return isNeedSplice
            ? (item.stockKey ?? item.stock_key)
                .split(':')
                .slice(0, -1)
                .map((i, idx) => {
                  if (idx == 2) {
                    return Number(i)
                  } else {
                    return i
                  }
                })
                .join(':') + `:${size}`
            : stock_key.substring(0, stock_key.lastIndexOf(':'))
        })
    )
  )
  let startRes = '0001'
  const mergeSetting =
    store.state.mergeFolderSetting ?? store.state.ncSetting.customizeFolder
  if (
    mergeSetting.platePropertyMergeFile &&
    mergeSetting.subfolderName.templateString.includes('文件数自增')
  ) {
    startRes = await getFileStartNum({
      plateTypeNum: stockKeyArr.length,
      fileStartNum: mergeSetting.fileStartNum,
      settingId:
        store.state.ncSetting.process_setting_id ??
        mergeSetting.process_setting_id,
    })
  }
  const startNum = startRes.data ?? startRes
  store.commit('setMergeSubFolderData', {
    date: `${mounth}${day}`,
    startNum,
    stockKeyArr,
  })
}

export function dealTransFileName(item, isNeedSlice = true) {
  // 文件合并 子文件夹名称 长宽不再是必须；注释掉下面判断
  // if (!item.size && !item.display_big_plank_size) {
  //   return ''
  // }
  const { subfolderNameStr } =
    store.state.mergeFolderSetting ?? store.state.ncSetting.customizeFolder
  const mergeSubFolderData = store.state.mergeSubFolderData
  const stockKeyArr = mergeSubFolderData.stockKeyArr
  const date = mergeSubFolderData.date
  let templateString = ''
  // 传进来的size不需要厚度
  let sizeList =
    item.size?.split('*')?.splice(0, 2) ??
    item.display_big_plank_size?.split('*')?.splice(0, 2)
  // 文件夹名称内不能出现*特殊符号
  let plankSize = sizeList ? `${sizeList[0]}×${sizeList[1]}` : ''
  const partKeysArr = isNeedSlice
    ? item.stock_key.split(':').slice(0, -1)
    : item.stock_key.split(':')
  const needSizeArr = ['板件长宽', '文件数自增']
  const stockKeyStr =
    needSizeArr.some((item) => subfolderNameStr.includes(item)) && plankSize
      ? partKeysArr.join(':') + `:${plankSize}`
      : partKeysArr.join(':')
  const stockNo = stockKeyArr.indexOf(stockKeyStr)
  if (stockNo != -1) {
    let finalNo = Number(mergeSubFolderData.startNum) + stockNo
    const subLenth =
      String(mergeSubFolderData.startNum).length - String(finalNo).length
    if (subLenth > 0) {
      finalNo = '0'.repeat(subLenth) + finalNo
    }
    const subFolderMap = new Map([
      ['{输出日期}', date],
      ['{文件数自增}', finalNo],
      ['{板件颜色}', partKeysArr[0]],
      ['{板件类型}', dealHighlightMatCode(partKeysArr[1])],
      ['{板件厚度}', partKeysArr[2]],
      ['{板件长宽}', plankSize],
    ])

    const folderSetting =
      store.state.mergeFolderSetting ?? store.state.ncSetting.customizeFolder
    templateString = folderSetting.subfolderName.templateString + '/'
    subFolderMap.forEach((val, key) => {
      templateString = templateString.replace(key, val)
    })
  }
  return templateString
}

/**
 * 把promise文件转换成zip文件
 * @param {*} zip
 * @param {*} promises
 * @param {*} zipName
 * @param {*} thenCb
 * @param {*} finalCb
 * @param {*} imgZip 生成的imgZip对象
 * @param {*} ncSuffix 贴标文件后缀
 * @param {*} ncSetting
 * @param {*} isOpenSingleFile 开启生成单个文件(后台设置的(贴标管理))
 */
export async function promiseToZip(
  zip,
  promises,
  zipName,
  thenCb,
  finalCb,
  that,
  ncSetting
) {
  const { imgZip, ncLifeFuncObj } = that
  const { genZip } = ncLifeFuncObj ?? {}
  // 获取下载NC中的标签图片Zip路径，并移除掉
  removeTagImageZip(zip, imgZip, ncSetting)

  zip.generateAsync({ type: 'blob' }).then(async (content) => {
    // 生成二进制流 将二进制流转化为文件对象
    let time = new Date()
    let ncName = `/bluenMaterialData/saw_${time.getTime()}.zip`
    let file = new File([content], ncName, {
      type: '',
      lastModified: Date.now(),
    })
    Vue.prototype.$getByToken('/get_oss_token', {}, (res) => {
      if (res.status == 1) {
        let client = new OSS({
          region: 'oss-cn-hangzhou',
          accessKeyId: res.data.AccessKeyId,
          accessKeySecret: res.data.AccessKeySecret,
          stsToken: res.data.SecurityToken,
          bucket: 'eggrj',
        })
        client
          .multipartUpload(ncName, file)
          .then(() => {
            let url = 'https://eggrj.oss-cn-hangzhou.aliyuncs.com' + ncName
            if (typeof thenCb === 'function') {
              thenCb(url)
            }
          })
          .catch(() => {
            Vue.prototype.$message({
              type: 'error',
              message: '上传失败!',
            })
          })
      }
    })
    let flag = false
    // 加号在使用 new URL解析时会解析成空格导致彩标无法找到文件
    zipName = zipName.replace(/\+/g, '_')
    // 是否采用zip的形式导出 否则是采用文件直接写入
    if (store.state.preferencesSetting.setting?.is_use_folder_export) {
      flag = await savaFileToDisk(zip, zipName)
    }
    if (!flag) {
      FileSaver.saveAs(content, `${zipName}.zip`) // 利用file-saver保存文件
    }
    genZip && genZip('end')
    that.isShowNCTask = false
    if (typeof finalCb === 'function') {
      finalCb()
    }
    // 延迟打开彩标避免一些意外的情况
    setTimeout(() => {
      const { isFollowExportTag, isSendToEzSort, currentTagType } =
        store.state.preferencesSetting.setting
      if (isSendToEzSort && isFollowExportTag && currentTagType.length) {
        transformToEzSort.processDownloadOpenEzsort(zipName)
      }
    }, 1500)
  })
}

// 将data数据保存到Oss
export async function saveDataJsonToOss(
  data,
  cb,
  oriName = '',
  path = '/bluenMaterialData/operateRecord/'
) {
  // 对 /bluenMaterialData 目录下的 json 文件进行 gzip 压缩
  const compressedBlob = await compressJsonToGzip(data)
  // 存储
  const name =
    path + oriName + new Date().getTime() + '_' + 'operateRecord' + '.json.gz'
  let file = new File([compressedBlob], name, {
    type: '',
    lastModified: Date.now(),
  })
  getByToken('/get_oss_token', {}, (res) => {
    if (res.status == 1) {
      let client = new OSS({
        region: 'oss-cn-hangzhou',
        accessKeyId: res.data.AccessKeyId,
        accessKeySecret: res.data.AccessKeySecret,
        stsToken: res.data.SecurityToken,
        bucket: 'eggrj',
      })
      client
        .multipartUpload(name, file, {
          headers: {
            'Content-Type': 'application/json',
            'Content-Encoding': 'gzip',
          },
        })
        .then(() => {
          let url = 'https://eggrj.oss-cn-hangzhou.aliyuncs.com' + name
          if (typeof cb === 'function') {
            cb(url)
          }
        })
        .catch((err) => {
          console.error(err)
          Message({
            type: 'error',
            info: '上传失败!',
            duration: 1500,
            offset: 60,
          })
        })
    } else {
      Message({
        type: 'error',
        info: '身份信息已过期, 请重新从柜柜跳转!',
        duration: 1500,
        offset: 60,
      })
    }
  })
}

// 为了操作记录保存到oss
export function saveDataJsonToOssForRecord(record, type, option = {}, cb) {
  saveDataJsonToOss(
    option.save_data,
    (jsonUrl) => {
      saveDataJsonToOss(record, (url) => {
        let obj = {
          uid: store.state.userInfo.id,
          // setting_id: this.activeProductId,
          action_type: type,
          action_url: url,
          // 用于还原设备数据
          operate_equipment: option.operate_equipment || '', // 修改的设备
          save_data: jsonUrl || '', // 保存的数据
          setting_id: option.setting_id || 0, // 产品线id
          operator_phone: option.operatorPhone || '', // 后台应用生产线设置时，记录操作人的uid
        }
        if (option?.id) {
          obj.setting_id = option.id
        }
        obj.process_line_name =
          option?.name ||
          getUrlParams('line') ||
          store.state.currentProductLineName
        postByToken('/save_operation_record', obj, () => {
          if (typeof cb === 'function') {
            cb()
          }
        })
      })
    },
    'savedata'
  )
}

// 处理五六面钻分文件
export function dealGroupFile(file, fileName) {
  let nameArr = fileName.split('/')
  const order_code = file.group_file ? file.order_code : null
  if (!order_code) return fileName
  const last = nameArr[nameArr.length - 1]
  return [...nameArr.slice(0, -1), order_code, last].join('/')
}

async function tagFileExport(
  tagFile,
  zip,
  cache,
  name,
  folderSetting,
  isExcel = false
) {
  const isJinxiPaiban = router.currentRoute.query?.is_jinxi_paiban === '1'
  const { is_gen_surplus_folder } = folderSetting
  let translateFileName = _translateFileNameFn(
    '标签图',
    folderSetting,
    'label_image_folder',
    tagFile.isOnlySurplus && tagFile
  )
  const surplusFolder = _translateFileNameFn(
    '余料大板文件夹',
    folderSetting,
    'surplus_folder',
    tagFile.isOnlySurplus && tagFile
  )
  translateFileName =
    is_gen_surplus_folder && tagFile.isOnlySurplus
      ? surplusFolder
        ? `${surplusFolder}/${translateFileName}`
        : ''
      : translateFileName
      ? `${translateFileName}`
      : ''
  translateFileName = translateFileName ? translateFileName + '/' : ''
  // let translateFileName = '标签图'
  let filename = translateFileName + name + `.${tagFile?.exportType}`
  if (isExcel) filename = name + `.${tagFile?.exportType}`
  // 开启 将雕刻机正/反面文件用不同文件夹放置 功能后 贴标图片（非pdf）不再生成单独的文件 而是放入下料文件F中
  if (
    folderSetting.engraving_front_back_folder &&
    tagFile?.exportType !== 'pdf'
  ) {
    filename = ''
    return
  }
  const fr = new FileReader()
  await fr.readAsArrayBuffer(tagFile.fileData)
  return new Promise((resolve) => {
    fr.addEventListener('loadend', (event) => {
      const res = event.target.result
      zip.file(filename, res, { binary: true }) // 逐个添加文件
      cache[filename] = res
      resolve()
    })
  })
}

/**
 * @description 检查是否是否需要生成一体机的单独文件夹
 * @param {object} folderSetting 自定义文件设置
 * @returns
 */
function isUseAioFolder(folderSetting, ncData) {
  return (
    folderSetting?.required_folders?.includes('aio_folder') &&
    ncData?.aio_file_type === 'aio'
  )
}

export async function saveDataToOSS(data, fileName, path) {
  /** 时间戳加载 filterObj 文件名 */
  let jsonFile = jsonToFile(data, fileName)
  // 如果 path 是 bluenMaterialData，则进行 gzip 压缩
  if (path === 'bluenMaterialData') {
    const compressedBlob = await compressJsonToGzip(data)
    // 确保文件名以 .gz 结尾
    const gzFileName = fileName.endsWith('.gz') ? fileName : fileName + '.gz'
    jsonFile = new File([compressedBlob], gzFileName, {
      type: '',
      lastModified: Date.now(),
    })

    // 使用封装好的 uploadCompressedFile 函数上传
    // 判断 fileName 是否已经是完整路径（以 / 开头）
    const customPath = fileName.startsWith('/') ? gzFileName : undefined
    return uploadCompressedFile(jsonFile, customPath)
  }
  // 其他路径使用原来的 uploadFileToOss
  const filterObjUrl = await uploadFileToOss(
    jsonFile,
    undefined,
    undefined,
    path
  )
  return filterObjUrl
}

/** @description 抽离保存文件删除内部的保存了图片的 zip 文件 （未知意义） */
export function removeTagImageZip(zip, imgZip, ncSetting) {
  if (imgZip && Object.keys(imgZip).length) {
    let zipPath = ''
    let translateFileName = _translateFileNameFn(
      '标签图',
      ncSetting?.customizeFolder,
      'label_image_folder'
    )
    Object.keys(zip.files).forEach((key) => {
      if (key.startsWith(translateFileName) && key.endsWith('.zip')) {
        zipPath = key
      }
    })
    zipPath && zip.remove(zipPath)
  }
}
